import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { environment } from '../../../environments/environment';
import { LoggerService } from '../services/logger.service';
import { sanitizeUrl } from '@core/utils/url-sanitizer';

/**
 * GAP-FE-A50: feature-flag route guard factory.
 *
 * Returns a `CanActivateFn` that allows navigation only when the named
 * `environment.features.<key>` flag is strictly `true`. Any other value
 * (`false`, `undefined`, truthy non-boolean, or a missing `features`
 * block entirely) is treated as DISABLED and the guard redirects to the
 * NotFoundComponent surface so a disabled feature is indistinguishable
 * from a route that does not exist.
 *
 * The redirect lands on the global `**` wildcard at `app.routes.ts:483`,
 * which loads `NotFoundComponent`. There is no named `/not-found` route
 * — that's intentional: relying on the wildcard means any future
 * literal `/not-found` route addition is detected by the regression
 * spec at `feature-flag.guard.spec.ts`.
 *
 * This complements (does NOT replace) the existing `authGuard +
 * tenantGuard + roleGuard` chain. Feature-flag enforcement runs BEFORE
 * the role guard so a user who navigates to a disabled URL via the
 * address bar receives the same 404 surface as any other invalid URL.
 *
 * Companion gating sites — when adding a new flag-gated route, update
 * ALL of:
 *   1. `app.routes.ts` — add `featureFlagGuard('enableX')` to `canActivate`
 *   2. `shared/components/sidebar-modern/sidebar-modern.component.ts`
 *      (FLAG_GATED_ITEM_IDS) — gate the sidebar entry
 *   3. `core/services/command-palette.service.ts`
 *      (COMMAND_PALETTE_GATED_PATHS) — gate the palette entry
 *   4. Render the host widget conditionally (e.g.
 *      `approval-queue-widget.component.ts` reads the flag and renders
 *      `<ng-container/>` when disabled)
 *
 * Design rationale (per docs/gaps-frontend-2026-05-23.md GAP-FE-A50):
 *   Flag-gated surfaces must behave as if they do not exist while the
 *   flag is off — no partial UI, no teaser banners. (Historical note,
 *   GAP-050: the copilot/agent backends this guard originally fenced
 *   off as 501 deferred-stubs are now implemented and mounted in
 *   `routes/copilot.rs` / `routes/agent.rs`; the flags remain the
 *   rollout switch for the FE surfaces.)
 *
 * Usage in app.routes.ts:
 *   canActivate: [authGuard, tenantGuard, featureFlagGuard('enableAgentProposals'), roleGuard]
 */
export type FeatureFlagKey = keyof typeof environment.features;

/**
 * The unique flag key this guard checks. Attached to the returned
 * function so route specs can verify the correct flag is wired up to
 * each route (e.g. that /agent-proposals uses 'enableAgentProposals',
 * not 'enableCopilotSummaries').
 */
export interface FeatureFlagGuardFn extends CanActivateFn {
  readonly flagKey: FeatureFlagKey;
}

export function featureFlagGuard(flagKey: FeatureFlagKey): FeatureFlagGuardFn {
  const guard: CanActivateFn = (
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree => {
    const router = inject(Router);
    const logger = inject(LoggerService);

    // Defensive: `environment.features` may be undefined if the env
    // config is misconfigured. Treat any non-strict-`true` value as
    // disabled.
    const enabled = environment.features?.[flagKey] === true;

    if (enabled) {
      return true;
    }

    logger.info('GAP-FE-A50 feature-flag disabled — redirecting to NotFound', {
      flagKey,
      attemptedUrl: sanitizeUrl(state?.url ?? ''),
    });

    return router.createUrlTree(['/not-found']);
  };

  return Object.assign(guard, { flagKey }) as FeatureFlagGuardFn;
}
